import { memo, type ReactNode, useCallback, useMemo, useState } from 'react';
import { AutosaveStatus } from '@components/AutosaveStatus';
import { useAutoSave } from '@hooks/useAutoSave';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from '@mui/material';

import type { GeneratedIntakeField, IntakeFieldSuggestion, IntakeFieldValue } from '../../types';

import { chipSx, wf } from './tokens';

interface IntakePageFieldInputProps {
  field: GeneratedIntakeField;
  draftValue: IntakeFieldValue;
  acknowledgedValue: IntakeFieldValue;
  onDraftChange: (field: GeneratedIntakeField, value: IntakeFieldValue) => void;
  onPersist: (field: GeneratedIntakeField, value: IntakeFieldValue) => Promise<void>;
  suggestion?: IntakeFieldSuggestion;
  onAcceptSuggestion?: (suggestion: IntakeFieldSuggestion) => Promise<void>;
  onDiscardSuggestion?: (suggestion: IntakeFieldSuggestion) => void;
  disabled?: boolean;
  disabledReason?: string;
  saveMode?: 'autosave' | 'explicit';
  showStatus?: boolean;
}

const IMMEDIATE_SAVE_TYPES = new Set(['boolean']);
const SECRET_TYPES = new Set(['secret', 'ssn']);

function fieldControlType(dataType: string): 'date' | 'number' | 'password' | 'text' {
  if (dataType === 'date') return 'date';
  if (dataType === 'integer' || dataType === 'number') return 'number';
  if (SECRET_TYPES.has(dataType)) return 'password';
  return 'text';
}

function isSameValue(left: IntakeFieldValue, right: IntakeFieldValue): boolean {
  if (Array.isArray(left) && Array.isArray(right)) {
    return left.length === right.length && left.every((value, index) => value === right[index]);
  }
  return Object.is(left, right);
}

function displaySuggestionValue(value: IntakeFieldValue): string {
  if (Array.isArray(value)) return value.join(', ');
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  return String(value);
}

function hasDisplayedValue(value: IntakeFieldValue): boolean {
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === 'string') return value.length > 0;
  return true;
}

/**
 * A single generated Intake field with isolated autosave state. Persistence is
 * supplied by the generated page definition so this component never needs to infer a backend
 * destination from display metadata.
 */
function IntakePageFieldInputComponent({
  field,
  draftValue,
  acknowledgedValue,
  onDraftChange,
  onPersist,
  suggestion,
  onAcceptSuggestion,
  onDiscardSuggestion,
  disabled = false,
  disabledReason,
  saveMode = 'autosave',
  showStatus = true,
}: IntakePageFieldInputProps) {
  const [suggestionError, setSuggestionError] = useState(false);
  const [isAcceptingSuggestion, setIsAcceptingSuggestion] = useState(false);
  const dataType = field.data_type.toLowerCase();
  const isSecret = SECRET_TYPES.has(dataType);
  const isWritable = field.is_writable !== false;
  const isDirty = !isSameValue(draftValue, acknowledgedValue);
  const normalizeForPersistence = useCallback(
    (value: IntakeFieldValue): IntakeFieldValue => {
      if ((dataType === 'integer' || dataType === 'number') && typeof value === 'string') {
        return value.trim() === '' ? '' : Number(value);
      }
      if ((dataType === 'multi-enum' || dataType === 'multi-string') && typeof value === 'string') {
        return value
          .split(',')
          .map(entry => entry.trim())
          .filter(Boolean);
      }
      return value;
    },
    [dataType],
  );
  const persist = useCallback(
    async (value: IntakeFieldValue) => {
      try {
        await onPersist(field, normalizeForPersistence(value));
      } catch {
        // Keep backend bodies and secret values out of the shared autosave
        // hook's console error while still preserving a retryable failure.
        throw new Error('Unable to save intake field');
      }
    },
    [field, normalizeForPersistence, onPersist],
  );
  const { saveImmediately, triggerAutoSave, status, lastSavedAt } = useAutoSave<IntakeFieldValue>({
    onSave: persist,
    debounceMs: 800,
    enabled: saveMode === 'autosave' && !disabled && isWritable,
    flushOnUnmount: saveMode === 'autosave',
  });

  const displayedValue = useMemo(() => {
    if (isSecret && isSameValue(draftValue, acknowledgedValue)) return '';
    return draftValue;
  }, [acknowledgedValue, draftValue, isSecret]);

  const saveLatest = () => {
    if (isWritable && isDirty) saveImmediately(draftValue);
  };

  const pendingSuggestion =
    !isSecret && !isDirty && suggestion && !isSameValue(draftValue, suggestion.value)
      ? suggestion
      : null;

  const suggestionControl = pendingSuggestion ? (
    <Box
      role="status"
      aria-label={`Suggested value for ${field.label}`}
      sx={{
        minHeight: 58,
        border: `1px dashed ${wf.amberWarn}`,
        borderRadius: '6px',
        backgroundColor: wf.amberSoft,
        px: 1,
        py: 0.75,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
        <Box sx={{ color: wf.muted, fontSize: '10px', lineHeight: 1.2 }}>{field.label}</Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Box sx={{ ...chipSx('sug'), fontSize: '9px', px: 0.75 }}>Suggested</Box>
          <Button
            size="small"
            variant="text"
            onClick={() => onDiscardSuggestion?.(pendingSuggestion)}
            disabled={disabled || isAcceptingSuggestion || !onDiscardSuggestion}
            aria-label={`Discard suggested value for ${field.label}`}
            sx={{ minWidth: 0, px: 0.75, py: 0, fontSize: '10px', textTransform: 'none' }}
          >
            Discard
          </Button>
          <Button
            size="small"
            variant="outlined"
            onClick={() => {
              if (!onAcceptSuggestion) return;
              setSuggestionError(false);
              setIsAcceptingSuggestion(true);
              void onAcceptSuggestion(pendingSuggestion)
                .then(() => onDraftChange(field, pendingSuggestion.value))
                .catch(() => setSuggestionError(true))
                .finally(() => setIsAcceptingSuggestion(false));
            }}
            disabled={disabled || isAcceptingSuggestion || !onAcceptSuggestion}
            aria-label={`Accept suggested value for ${field.label}`}
            sx={{ minWidth: 0, px: 0.75, py: 0, fontSize: '10px', textTransform: 'none' }}
          >
            {isAcceptingSuggestion ? 'Accepting…' : 'Accept'}
          </Button>
        </Box>
      </Box>
      <Box sx={{ color: wf.ink, fontSize: '12px', mt: 0.25 }}>
        {displaySuggestionValue(pendingSuggestion.value)}
      </Box>
      {suggestionError && (
        <Box sx={{ color: 'error.main', fontSize: '10px', mt: 0.5 }}>
          This suggestion changed. Refresh the document and try again.
        </Box>
      )}
    </Box>
  ) : null;

  const wrapControl = (control: ReactNode) => (
    <Box sx={{ position: 'relative' }}>
      <Box
        aria-hidden={pendingSuggestion ? true : undefined}
        inert={pendingSuggestion ? true : undefined}
        sx={pendingSuggestion ? { opacity: 0, pointerEvents: 'none' } : undefined}
      >
        {control}
      </Box>
      {pendingSuggestion && (
        <Box sx={{ position: 'absolute', inset: 0, zIndex: 1 }}>{suggestionControl}</Box>
      )}
    </Box>
  );

  const statusControl = !showStatus ? null : saveMode === 'explicit' ? (
    <Box
      aria-live="polite"
      sx={{ minHeight: 28, color: 'text.secondary', fontSize: '0.75rem', textAlign: 'right' }}
    >
      {isDirty ? 'Unsaved change' : null}
    </Box>
  ) : (
    <Box
      aria-live="polite"
      sx={{
        display: 'flex',
        minHeight: 28,
        alignItems: 'center',
        justifyContent: 'flex-end',
        gap: 1,
      }}
    >
      <AutosaveStatus
        status={status}
        isDirty={isDirty}
        lastSavedAt={lastSavedAt}
        messages={{ error: 'Could not save this field' }}
        verbose
      />
      {status === 'error' && (
        <Button size="small" onClick={saveLatest} disabled={disabled}>
          Retry save
        </Button>
      )}
    </Box>
  );

  if (dataType === 'file') return null;

  if (disabledReason) {
    return wrapControl(
      <Alert severity="warning" aria-label={`${field.label} unavailable`}>
        <Box sx={{ fontWeight: 600 }}>{field.label}</Box>
        <Box>{disabledReason}</Box>
      </Alert>,
    );
  }

  if (!isWritable) {
    const reason =
      field.destination.kind === 'secure_collection_deferred' ? field.destination.reason : null;
    const providerRosterMessage =
      field.destination.kind === 'provider_roster'
        ? 'Your JotPsych specialist manages this provider roster with your team.'
        : null;
    return wrapControl(
      <Alert severity="info" aria-label={`${field.label} unavailable`}>
        <Box sx={{ fontWeight: 600 }}>{field.label}</Box>
        <Box>
          {reason ??
            providerRosterMessage ??
            'This information is not available for editing in Intake Hub yet.'}
        </Box>
      </Alert>,
    );
  }

  if (dataType === 'boolean') {
    return wrapControl(
      <Box>
        <FormControlLabel
          control={
            <Checkbox
              checked={Boolean(draftValue)}
              disabled={disabled}
              required={field.is_required}
              onChange={event => {
                const nextValue = event.target.checked;
                onDraftChange(field, nextValue);
                if (saveMode === 'autosave') saveImmediately(nextValue);
              }}
            />
          }
          label={field.label}
        />
        {field.help_text && (
          <Box sx={{ color: 'text.secondary', fontSize: '0.75rem', ml: 4.5 }}>
            {field.help_text}
          </Box>
        )}
        {statusControl}
      </Box>,
    );
  }

  if ((field.options?.length ?? 0) > 0) {
    const isMultiple = dataType === 'multi-enum';
    const selectValue = isMultiple
      ? Array.isArray(draftValue)
        ? draftValue
        : []
      : Array.isArray(draftValue)
        ? (draftValue[0] ?? '')
        : draftValue;

    return wrapControl(
      <Box>
        <FormControl fullWidth size="small" required={field.is_required} disabled={disabled}>
          <InputLabel id={`${field.field_id}-options-label`}>{field.label}</InputLabel>
          <Select
            labelId={`${field.field_id}-options-label`}
            label={field.label}
            multiple={isMultiple}
            value={selectValue}
            onChange={event => {
              const nextValue = event.target.value;
              onDraftChange(field, nextValue);
              if (saveMode === 'autosave') saveImmediately(nextValue);
            }}
          >
            {field.options?.map(option => (
              <MenuItem key={option} value={option}>
                {option}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        {field.help_text && (
          <Box sx={{ color: 'text.secondary', fontSize: '0.75rem', mt: 0.5, ml: 1.75 }}>
            {field.help_text}
          </Box>
        )}
        {statusControl}
      </Box>,
    );
  }

  const inputType = fieldControlType(dataType);

  return wrapControl(
    <Box>
      <TextField
        fullWidth
        size="small"
        label={field.label}
        type={inputType}
        value={
          typeof displayedValue === 'boolean'
            ? String(displayedValue)
            : Array.isArray(displayedValue)
              ? displayedValue.join(', ')
              : displayedValue
        }
        required={field.is_required}
        disabled={disabled}
        helperText={field.help_text || undefined}
        onChange={event => {
          const nextValue = event.target.value;
          onDraftChange(field, nextValue);
          if (saveMode === 'autosave' && !IMMEDIATE_SAVE_TYPES.has(dataType)) {
            triggerAutoSave(nextValue);
          }
        }}
        onBlur={saveMode === 'autosave' ? saveLatest : undefined}
        slotProps={{
          htmlInput: isSecret
            ? { autoComplete: 'new-password', 'aria-label': field.label }
            : { 'aria-label': field.label },
          inputLabel:
            inputType === 'date' || hasDisplayedValue(displayedValue)
              ? { shrink: true }
              : undefined,
        }}
      />
      {statusControl}
    </Box>,
  );
}

export const IntakePageFieldInput = memo(IntakePageFieldInputComponent);
